import React, { useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { getAmplifyConfig } from './aws.config';
import AuthWrapper from './components/AuthWrapper';
import gekkovetTheme from './theme';
import { FlashMessagesProvider } from './contexts/FlashMessagesContext';
import { 
  SharedComponentsConfigurationProvider,
  SharedComponentsConfiguration 
} from '@gekkovet-oy/shared-components';

// Configure Amplify with environment-specific settings
Amplify.configure(getAmplifyConfig());

// Configure shared components
const sharedComponentsConfig: SharedComponentsConfiguration = {
  gekkoApiBaseUrl: process.env.AWS_API_ENDPOINT || 'https://api.dev-gekkovet.com/v1',
  websocketUrl: process.env.GEKKO_API_WS_ENDPOINT || 'wss://api.dev-gekkovet.com/ws',
  environment: process.env.ENVIRONMENT === 'prod' ? 'production' : 'development',
};

const App: React.FC = () => {
  useEffect(() => {
    // Log current configuration for debugging (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('Environment:', process.env.NODE_ENV);
      console.log('AWS Region:', process.env.AWS_REGION);
      console.log('User Pool ID:', process.env.AWS_USER_POOL_ID);
    }
  }, []);

  return (
    <SharedComponentsConfigurationProvider config={sharedComponentsConfig}>
      <ThemeProvider theme={gekkovetTheme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            // Only show background image on desktop
            backgroundImage: { xs: 'none', md: `url(${new URL('./assets/background.jpg', import.meta.url).href})` },
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            position: 'relative',
            // White background on mobile
            bgcolor: { xs: 'background.default', md: 'transparent' },
          }}
        >
          {/* Transparent overlay - only on desktop */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: { xs: 'transparent', md: 'rgba(255, 255, 255, 0.25)' }, // Light white overlay only on desktop
              zIndex: 0,
              display: { xs: 'none', md: 'block' },
            }}
          />
          
          {/* Main content with higher z-index */}
          <Box sx={{ position: 'relative', zIndex: 1, height: '100vh' }}>
            <FlashMessagesProvider>
              <AuthWrapper />
            </FlashMessagesProvider>
          </Box>
        </Box>
      </ThemeProvider>
    </SharedComponentsConfigurationProvider>
  );
};

export default App;